import Badge from "../_common/components/Badge";
import ButtonSpinner from "../_common/components/buttons/buttonSpinner/buttonSpinner";
import MatriculacionModal from "../matriculacion-modal/matriculacion-modal";
import Modal from "../_common/components/Modal";
import crypto from "crypto";
import { h, Fragment } from "preact";
import { isSpanishUser } from "../_common/utils/getLang";
import { t } from "../_i18n";
import { toastService } from "../_services/toast-service";
import { useState, useEffect } from "preact/hooks";
import { isTwMd } from "../_common/common";

import "./sales-banner.scss";

export default function SalesBanner({
  buttonLabel,
  discountBadge,
  discountBadge1,
  discountSubtitle,
  discountTitle,
  discountTitle1,
  discountTitle2,
  endDate,
  modalTitle,
  showCountdown,
  startDate,
  studentMeta,
  user,
  style,
}) {
  /** Variables **/
  const isPremiumUser =
    studentMeta?.has_whatsapp && studentMeta?.shop_id !== 24;
  const isMad = studentMeta?.formative_zone === "MAD";
  const semesterId = isMad ? 90 : 84;
  const isSpanish = isSpanishUser(studentMeta?.shop_id);
  const isOnline = studentMeta?.shop_id === 1;

  /** States **/
  const [timeLeft, setTimeLeft] = useState({
    days: "00",
    hours: "00",
    minutes: "00",
    seconds: "00",
  });
  const [showModal, setShowModal] = useState(false);
  const [loadingSkeleton, setLoadingSkeleton] = useState(false);
  const [showSalesBannerBanner, setShowSalesBannerBanner] = useState(false);
  const [loadingContinue, setLoadingContinue] = useState(false);
  const [cartData, setCartData] = useState(null);
  const [premiumSelected, setPremiumSelected] = useState(isPremiumUser);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [cartDataAlreadyLoaded, setCartDataAlreadyLoaded] = useState();
  const [toValidate, setToValidate] = useState();
  const [generatedMadCartURL, setGeneratedMadCartURL] = useState(null);

  /** Lifcycles **/
  useEffect(() => {
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    (async () => {
      const result = await checkSalesBanner();
      setShowSalesBannerBanner(result);
    })();
  }, []);

  /** Methods **/
  const normalizeDate = (date, endOfDay = false) => {
    return new Date(
      date?.year,
      date?.month - 1,
      date?.day,
      endOfDay ? 23 : 0,
      endOfDay ? 59 : 0,
      endOfDay ? 59 : 0,
    );
  };

  const startDateIsBeforeEndDay = () => {
    const start = startDate
      ? normalizeDate(startDate).getTime()
      : Number.NEGATIVE_INFINITY;

    const end = endDate
      ? normalizeDate(endDate, true).getTime()
      : Number.POSITIVE_INFINITY;

    const now = Date.now();

    return now >= start && now <= end;
  };

  const checkSalesBanner = () => {
    if (!isSpanish || !isOnline) return false;

    try {
      const zoneCode = studentMeta.formative_zone_code;

      const program = (studentMeta.unfinished_academic_programs || []).find(
        (p) => p.formative_zone_code === zoneCode,
      );

      if (!program?.formative_actions) return false;

      const formativeActions = program.formative_actions;

      const allSubjects = formativeActions
        .map((formativeAction) => formativeAction?.shop_code?.trim())
        .filter((code) => code !== "");

      const studentSubjects = (studentMeta.coursing_or_finished_enrolls || [])
        .filter((enroll) => enroll.formative_zone_code === zoneCode)
        .map((enroll) => enroll?.formative_action?.shop_code?.trim())
        .filter((code) => code !== "");

      const missing = allSubjects.filter((code) => {
        const formativeAction = formativeActions.find(
          (a) => a?.shop_code?.trim() === code,
        );

        if (formativeAction?.is_enrollable === false) return false;

        return !studentSubjects.includes(code);
      });

      return missing.length >= 2;
    } catch (error) {
      console.error("Error en checkSalesBanner:", error);
      return false;
    }
  };

  const updateCountdown = () => {
    const now = Date.now();
    const end = normalizeDate(endDate, true).getTime();

    const distance = end - now;

    if (distance <= 0) {
      setTimeLeft({
        days: "00",
        hours: "00",
        minutes: "00",
        seconds: "00",
      });
      return;
    }

    const days = Math.floor(distance / (1000 * 60 * 60 * 24));
    const hours = Math.floor((distance / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((distance / (1000 * 60)) % 60);
    const seconds = Math.floor((distance / 1000) % 60);

    setTimeLeft({
      days: String(days).padStart(2, "0"),
      hours: String(hours).padStart(2, "0"),
      minutes: String(minutes).padStart(2, "0"),
      seconds: String(seconds).padStart(2, "0"),
    });
  };

  const encryptString = (studentId) => {
    const cipher = crypto.createCipheriv(
      micuentaenv.ENCRYPTION_METHOD,
      adaptKey(micuentaenv.API_ADMIN_KEYWORD),
      micuentaenv.IV_ENCRYPTION_STRING,
    );
    return encodeURIComponent(
      Buffer.from(
        cipher.update(studentId, "utf8", "base64") + cipher.final("base64"),
      ).toString(),
    );
  };

  const adaptKey = (key) => {
    const len = 32;
    if (key.length >= len) {
      return key.substring(0, len);
    } else {
      return key + Array(len - key.length + 1).join("\x00");
    }
  };

  const openModal = async () => {
    if (isMad) {
      if (loadingContinue) return;
      setLoadingContinue(true);
      const cart = await getCart();
      if (cart?.cartUrl) setGeneratedMadCartURL(cart.cartUrl);
      setLoadingContinue(false);
      return;
    }

    setShowModal(true);
  };

  const openNewWindow = () => {
    generatedMadCartURL && window.open(generatedMadCartURL, "_blank");
  };

  const getCart = async (products = []) => {
    try {
      let baseUrl =
        `${micuentaenv.STUDENT_ACCOUNT_API_basePath}/get_recommended_cart_black_friday` +
        `?s=${encryptString(user.id.toString())}` +
        `&as=${semesterId}`;

      if (products && Array.isArray(products) && products.length > 0) {
        const formattedProducts = products.filter((p) => !!p).join(",");
        const productsEncrypted = encryptString(formattedProducts);
        baseUrl += `&fa=${productsEncrypted}`;
      } else {
        const emptyEncrypted = encryptString("");
        baseUrl += `&fa=${emptyEncrypted}`;
      }

      const res = await fetch(baseUrl, {
        headers: { accept: "application/json" },
        method: "GET",
      });

      const data = await res.json();

      if (data.code && data.code !== 200) {
        throw new Error(data.message || "Ha ocurrido un error");
      }

      return data;
    } catch (error) {
      console.error("Error en getCart:", error?.message);
      toastService.showToast({
        message: "Se ha producido un error al cargar el carrito",
        type: "danger",
      });
      setShowModal(false);
      return null;
    }
  };

  const handleCloseModal = () => {
    setCartDataAlreadyLoaded(true);
    setShowModal(false);
  };

  const handleCalculateDiscount = async (products, initial) => {
    if (initial) {
      setLoadingSkeleton(true);
      const cart = await getCart();
      setLoadingSkeleton(false);

      const recommendedIds = getRecommendedProducts(cart);
      setSelectedProducts(recommendedIds);

      setCartData(cart);
    } else {
      setLoadingContinue(true);
      const cart = await getCart(products);
      setLoadingContinue(false);

      setCartData(cart);
    }
  };

  const handleContinue = async () => {
    if (cartData.cartUrl) {
      window.open(cartData.cartUrl, "_blank");
    }
  };

  const getRecommendedProducts = (cart) => {
    if (!cart?.semesters) return [];
    return cart.semesters?.flatMap(({ products }) => {
      return products
        .filter((product) => product.price > 0)
        .filter((product) => product.is_recommended)
        .map((product) => product.id_product);
    });
  };

  const hasInvalidTime =
    !timeLeft ||
    Object.values(timeLeft).some((v) => v === "NaN" || Number.isNaN(Number(v)));
  const buttonText = isMad
    ? generatedMadCartURL
      ? t("Ir al carrito con descuento")
      : t("¡Matricúlate ahora!")
    : buttonLabel;

  /** Return **/
  return (
    showSalesBannerBanner &&
    startDateIsBeforeEndDay() && (
      <Fragment>
        <div
          className={`flex-space-between flex flex-col bg-[${style?.backgroundColor}] p-8 text-white md:flex-row ${showCountdown ? "gap-6" : "gap-12"} m-x2-bottom`}
        >
          <div
            class={`flex flex-col gap-4 md:max-w-[${style?.textMaxWidth}px]`}
          >
            {discountTitle1 && discountTitle2 && isTwMd() ? (
              <div className="flex flex-col font-bold">
                <p className={`font-bold text-2xl md:text-4xl"}`}>{discountTitle1}</p>
                <p className={`font-bold text-2xl md:text-4xl"}`}>{discountTitle2}</p>
              </div>
            ) : (
              <p
                className={`font-bold leading-none text-2xl md:text-4xl
                  ${style?.textCenter && "my-auto"}
                `}
              >
                {discountTitle}
              </p>
            )}
            {discountSubtitle && <p>{discountSubtitle}</p>}
            <div>
              {discountBadge && (
                <Badge className="font-bold px-2 py-0 rounded-none text-black text-sm" type="dark-orange">
                  {discountBadge}
                </Badge>
              )}

              {discountBadge1 && (
                <Badge className="m-l font-bold px-2 py-0 rounded-none text-black text-sm" type="dark-orange">
                  {discountBadge1}
                </Badge>
              )}
            </div>
          </div>

          <div
            className={`flex flex-col gap-6 ${showCountdown ? "my-auto" : "mt-auto"}`}
          >
            {showCountdown && !hasInvalidTime && (
              <div>
                <strong className="block w-full text-center font-bold">
                  {t("La promoción acaba en...")}
                </strong>
                <div className="flex items-center justify-center gap-1">
                  <div>
                    <p className="text-2xl font-bold">{timeLeft.days}</p>
                    <p className="-mt-2">{t("días")}</p>
                  </div>
                  <div>
                    <p className="">
                      <strong>:</strong>
                    </p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{timeLeft.hours}</p>
                    <p className="-mt-2">{t("hrs")}</p>
                  </div>
                  <div>
                    <p className="">
                      <strong>:</strong>
                    </p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{timeLeft.minutes}</p>
                    <p className="-mt-2">{t("mins")}</p>
                  </div>
                  <div>
                    <p className="">
                      <strong>:</strong>
                    </p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{timeLeft.seconds}</p>
                    <p className="-mt-2">{t("segs")}</p>
                  </div>
                </div>
              </div>
            )}
            <ButtonSpinner
              showSpinner={loadingContinue}
              onClick={generatedMadCartURL ? openNewWindow : openModal}
              btnType='primary-highlight'
              style={isMad && generatedMadCartURL ? { backgroundColor: "#ff7f50" } : undefined}
              classNameContainer="w-full flex justify-center"
              className={`flex font-bold items-center justify-center min-w-full pb-3 pt-3.5 px-0 py-3 whitespace-nowrap md:min-w-[${style?.buttonWidth}px]`}
              iconClassName="text-black"
            >
              {buttonText}
            </ButtonSpinner>
          </div>
        </div>

        {showModal && (
          <Modal
            visible={showModal}
            closeModal={handleCloseModal}
            overlayClassName="modal-overlay sales-banner-overlay"
          >
            <MatriculacionModal
              cartData={cartData}
              cartDataAlreadyLoaded={cartDataAlreadyLoaded}
              isPremiumUser={isPremiumUser}
              loadingContinue={loadingContinue}
              loadingSkeleton={loadingSkeleton}
              onCalculateDiscount={handleCalculateDiscount}
              onCloseModal={handleCloseModal}
              onContinue={handleContinue}
              premiumSelected={premiumSelected}
              selectedProducts={selectedProducts}
              setLoadingContinue={setLoadingContinue}
              setPremiumSelected={setPremiumSelected}
              setSelectedProducts={setSelectedProducts}
              setToValidate={setToValidate}
              title={modalTitle}
              toValidate={toValidate}
            />
          </Modal>
        )}
      </Fragment>
    )
  );
}
